import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getFirestore, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import './Header.css';

const db = getFirestore();

const Header = ({ mode, onModeChange, cartCount, onCartClick }) => {
  const navigate = useNavigate();
  const { user, userData, logout, addresses, guestLocation, switchAddress, clearGuestLocation, userCollection, reloadAddresses, openAddressModal, openSignupModal, closeSignupModal } = useAuth();
  const [showAddressDropdown, setShowAddressDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showGuestAddressModal, setShowGuestAddressModal] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowAddressDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddressSelect = async (addr) => {
    switchAddress(addr);
    setShowAddressDropdown(false);

    // Mark selected address as default in Firestore
    if (user && addr.id && addr.id !== '__guest__') {
      try {
        for (const a of addresses) {
          if (a.isDefault && a.id !== addr.id) {
            await updateDoc(doc(db, userCollection, user.uid, 'addresses', a.id), {
              isDefault: false,
              updatedAt: serverTimestamp(),
            });
          }
        }
        await updateDoc(doc(db, userCollection, user.uid, 'addresses', addr.id), {
          isDefault: true,
          updatedAt: serverTimestamp(),
        });
        await reloadAddresses();
      } catch (error) {
        console.error('Error marking address as default:', error);
      }
    }
  };

  const handleRemoveGuestAddress = () => {
    clearGuestLocation();
    setShowGuestAddressModal(false);
    // If no saved addresses remain, redirect to home so the user can enter a new address
    if (addresses.length === 0) {
      navigate('/');
    }
  };

  const handleManageAddresses = () => {
    setShowAddressDropdown(false);
    setMobileMenuOpen(false);
    if (user) {
      navigate('/account');
    } else {
      setShowGuestAddressModal(true);
    }
  };

  // Build display list: saved addresses + guest location as a virtual entry
  const displayAddresses = [...addresses];
  if (guestLocation?.address && !addresses.some((a) => a.address === guestLocation.address)) {
    displayAddresses.unshift({
      id: '__guest__',
      address: guestLocation.address,
      label: 'Current',
      lat: guestLocation.lat,
      lng: guestLocation.lng,
      isDefault: false,
    });
  }

  const currentAddress = guestLocation?.address || (displayAddresses.length > 0 ? displayAddresses[0].address : null);
  const selectedAddress = displayAddresses.find((a) => a.address === currentAddress) || displayAddresses[0] || null;
  const showDropdown = displayAddresses.length > 0;

  return (
    <>
      <header className="ff-header">
        <div className="container ff-header-inner">
          <div className="ff-header-brand" onClick={() => navigate('/')}>
            <img src="/fodyfee_logo.png" alt="FodyFee" className="ff-header-logo" />
            <span className="ff-header-title">FodyFee</span>
          </div>

          <div className="ff-header-nav">
            <button className="ff-header-link" onClick={() => navigate('/explore')}>
              <i className="fas fa-search mr-1"></i> Explore
            </button>
            {user && (
              <button className="ff-header-link" onClick={() => navigate('/orders')}>
                <i className="fas fa-receipt mr-1"></i> Orders
              </button>
            )}
            {user && (
              <button className="ff-header-link" onClick={() => navigate('/reservations')}>
                <i className="fas fa-calendar-check mr-1"></i> Reservations
              </button>
            )}
            {user && (
              <button className="ff-header-link" onClick={() => navigate('/account')}>
                <i className="fas fa-user mr-1"></i> Account
              </button>
            )}
          </div>

          <div className="ff-header-actions">
            {typeof cartCount === 'number' && cartCount > 0 && (
              <button className="ff-header-cart" onClick={onCartClick}>
                <i className="fas fa-shopping-bag"></i>
                <span className="ff-header-cart-badge">{cartCount}</span>
              </button>
            )}

            {/* Address Dropdown */}
            {showDropdown && (
              <div className="ff-header-address-dropdown" ref={dropdownRef}>
                <button
                  className="ff-header-address-trigger"
                  onClick={() => setShowAddressDropdown((v) => !v)}
                >
                  <i className="fas fa-map-marker-alt"></i>
                  {selectedAddress?.label && (
                    <span className="ff-header-address-trigger-label">{selectedAddress.label}</span>
                  )}
                  <span className="ff-header-address-text">
                    {currentAddress
                      ? currentAddress.length > 30
                        ? currentAddress.substring(0, 30) + '...'
                        : currentAddress
                      : 'Select Address'}
                  </span>
                  <i className={`fas fa-chevron-down ff-header-address-chevron ${showAddressDropdown ? 'open' : ''}`}></i>
                </button>
                {showAddressDropdown && (
                  <div className="ff-header-address-menu">
                    <div className="ff-header-address-menu-header">
                      <i className="fas fa-map-marker-alt mr-1"></i> Delivery Location
                    </div>
                    {displayAddresses.map((addr) => (
                      <button
                        key={addr.id}
                        className={`ff-header-address-item ${guestLocation?.address === addr.address ? 'active' : ''}`}
                        onClick={() => handleAddressSelect(addr)}
                      >
                        <div className="ff-header-address-item-label">
                          <span className="ff-address-badge">{addr.label || 'Address'}</span>
                          {addr.isDefault && <span className="ff-address-default-tag">Default</span>}
                        </div>
                        <div className="ff-header-address-item-text">{addr.address}</div>
                        {guestLocation?.address === addr.address && (
                          <i className="fas fa-check-circle ff-address-check"></i>
                        )}
                      </button>
                    ))}
                    <div className="ff-header-address-menu-footer">
                      <button className="ff-header-address-manage" onClick={handleManageAddresses}>
                        <i className="fas fa-cog mr-1"></i> Manage Addresses
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* User name + Log Out (desktop only) */}
            {user && (
              <>
                <span className="ff-header-user-name-desktop" style={{ fontWeight: 700, marginRight: 8 }}>
                  {userData?.name || user.displayName || user.email}
                </span>
                <button className="ff-header-logout-desktop" onClick={() => navigate('/logout')}>
                  Log Out
                </button>
              </>
            )}
            {!user && (
              <button className="ff-header-signin-desktop" onClick={() => openSignupModal()}>
                Sign In
              </button>
            )}

            {/* Hamburger menu button (mobile only) */}
            <button
              className="ff-header-hamburger"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label="Menu"
            >
              <i className={`fas ${mobileMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
            </button>

            {mobileMenuOpen && (
              <div className="ff-header-mobile-menu">
                {/* Mobile menu header with greeting */}
                <div className="ff-header-mobile-menu-header">
                  {user && (
                    <div className="ff-header-mobile-greeting">
                      Hello, <strong>{userData?.name || user.displayName || 'Guest'}</strong>!
                    </div>
                  )}
                  {/* Always show current address in mobile header */}
                  {currentAddress && (
                    <div className="ff-header-mobile-current-address">
                      <i className="fas fa-map-marker-alt"></i>
                      <span>{currentAddress.length > 40 ? currentAddress.substring(0, 40) + '...' : currentAddress}</span>
                    </div>
                  )}
                </div>

                {/* Mobile address section */}
                <div className="ff-header-mobile-address-section">
                  <button className="ff-header-mobile-address-manage" onClick={handleManageAddresses}>
                    <i className="fas fa-cog mr-1"></i> Manage Addresses
                  </button>
                </div>

                <button className="ff-header-mobile-link" onClick={() => { setMobileMenuOpen(false); navigate('/explore'); }}>
                  <i className="fas fa-search mr-2"></i> Explore
                </button>
                {user && (
                  <button className="ff-header-mobile-link" onClick={() => { setMobileMenuOpen(false); navigate('/orders'); }}>
                    <i className="fas fa-receipt mr-2"></i> Orders
                  </button>
                )}
                {user && (
                  <button className="ff-header-mobile-link" onClick={() => { setMobileMenuOpen(false); navigate('/reservations'); }}>
                    <i className="fas fa-calendar-check mr-2"></i> Reservations
                  </button>
                )}
                {user && (
                  <button className="ff-header-mobile-link" onClick={() => { setMobileMenuOpen(false); navigate('/account'); }}>
                    <i className="fas fa-user mr-2"></i> Account
                  </button>
                )}
                {user ? (
                  <button className="ff-header-mobile-link" onClick={() => { setMobileMenuOpen(false); navigate('/logout'); }}>
                    <i className="fas fa-sign-out-alt mr-2"></i> Log Out
                  </button>
                ) : (
                  <button className="ff-header-mobile-link" onClick={() => { setMobileMenuOpen(false); openSignupModal(); }}>
                    <i className="fas fa-sign-in-alt mr-2"></i> Sign In
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Guest Address Management Modal */}
      {showGuestAddressModal && (
        <div className="ff-modal-overlay" onClick={() => setShowGuestAddressModal(false)}>
          <div className="ff-modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="ff-modal-header">
              <h3><i className="fas fa-map-marker-alt mr-2 text-orange"></i>Your Address</h3>
              <button className="ff-modal-close" onClick={() => setShowGuestAddressModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="ff-modal-body">
              {guestLocation?.address ? (
                <div className="alert alert-info">
                  <i className="fas fa-map-marker-alt mr-2"></i>
                  <strong>{guestLocation.address}</strong>
                </div>
              ) : (
                <p className="text-muted">No address set.</p>
              )}
              <div className="d-flex gap-2 mt-3">
                <button
                  className="ff-btn ff-btn-secondary ff-btn-block"
                  onClick={() => {
                    setShowGuestAddressModal(false);
                    openAddressModal();
                  }}
                >
                  <i className="fas fa-plus mr-2"></i>Add New Address
                </button>
                {guestLocation?.address && (
                  <button
                    className="ff-btn ff-btn-danger ff-btn-block"
                    onClick={handleRemoveGuestAddress}
                  >
                    <i className="fas fa-trash-alt mr-2"></i>Remove
                  </button>
                )}
              </div>
            </div>
            <div className="ff-modal-footer">
              <button className="ff-btn ff-btn-primary ff-btn-block" onClick={() => setShowGuestAddressModal(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
